import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { PrivateStorageModel } from '@models/private-storage.model';
import { PrivateStorageActions } from '@states/private-storage/private-storage.action-types';

export interface PrivateStorageState extends EntityState<PrivateStorageModel.Document> {
  orgDefaultId: string | null;
  isInitialLoaded: boolean;
  isSaving: boolean;
  deletingIds: string[];
  locationSelection: PrivateStorageModel.API.LocationSelectionGetResponse | null;
  isLocationSelectionLoading: boolean;
  isLocationSelectionSaving: boolean;
  isTesting: boolean;
  lastTestConnection: PrivateStorageModel.API.TestConnectionResponse | null;
}

export const adapter: EntityAdapter<PrivateStorageModel.Document> = createEntityAdapter<PrivateStorageModel.Document>({
  selectId: (document: PrivateStorageModel.Document) => document._id,
});

const initialState: PrivateStorageState = adapter.getInitialState({
  orgDefaultId: null,
  isInitialLoaded: false,
  isSaving: false,
  deletingIds: [],
  locationSelection: null,
  isLocationSelectionLoading: false,
  isLocationSelectionSaving: false,
  isTesting: false,
  lastTestConnection: null,
});

export const privateStorageStateReducer = createReducer(
  initialState,
  on(PrivateStorageActions.getOptionsSuccess, (state, { documents }) => {
    return adapter.setAll(documents, {
      ...state,
      isInitialLoaded: true,
    });
  }),
  on(PrivateStorageActions.getOptionsFail, (state) => {
    return {
      ...state,
      isInitialLoaded: true,
    };
  }),
  on(
    PrivateStorageActions.createOption,
    PrivateStorageActions.updateOption,
    PrivateStorageActions.setOrgDefault,
    PrivateStorageActions.clearOrgDefault,
    (state) => {
      return {
        ...state,
        isSaving: true,
      };
    }),
  on(
    PrivateStorageActions.createOptionFail,
    PrivateStorageActions.updateOptionFail,
    PrivateStorageActions.setOrgDefaultFail,
    PrivateStorageActions.clearOrgDefaultFail,
    (state) => {
      return {
        ...state,
        isSaving: false,
      };
    }),
  on(PrivateStorageActions.createOptionSuccess, PrivateStorageActions.updateOptionSuccess, (state, { document }) => {
    return adapter.upsertOne(document, {
      ...state,
      isSaving: false,
    });
  }),
  on(PrivateStorageActions.deleteOption, (state, { id }) => {
    return {
      ...state,
      deletingIds: [...state.deletingIds, id],
    };
  }),
  on(PrivateStorageActions.deleteOptionSuccess, (state, { id }) => {
    return adapter.removeOne(id, {
      ...state,
      deletingIds: state.deletingIds.filter(deletingId => deletingId !== id),
      orgDefaultId: state.orgDefaultId === id ? null : state.orgDefaultId,
    });
  }),
  on(PrivateStorageActions.deleteOptionFail, (state, { id }) => {
    return {
      ...state,
      deletingIds: state.deletingIds.filter(deletingId => deletingId !== id),
    };
  }),
  on(PrivateStorageActions.getOrgDefaultSuccess, (state, { response }) => {
    return {
      ...state,
      orgDefaultId: response.defaultPrivateStorageId ?? null,
    };
  }),
  on(PrivateStorageActions.setOrgDefaultSuccess, (state, { response }) => {
    return {
      ...state,
      isSaving: false,
      orgDefaultId: response.defaultPrivateStorageId ?? null,
    };
  }),
  on(PrivateStorageActions.clearOrgDefaultSuccess, (state) => {
    return {
      ...state,
      isSaving: false,
      orgDefaultId: null,
    };
  }),
  on(PrivateStorageActions.getLocationSelection, (state) => {
    return {
      ...state,
      isLocationSelectionLoading: true,
    };
  }),
  on(PrivateStorageActions.getLocationSelectionSuccess, (state, { selection }) => {
    return {
      ...state,
      locationSelection: selection,
      isLocationSelectionLoading: false,
    };
  }),
  on(PrivateStorageActions.getLocationSelectionFail, (state) => {
    return {
      ...state,
      isLocationSelectionLoading: false,
    };
  }),
  on(PrivateStorageActions.setLocationSelection, (state) => {
    return {
      ...state,
      isLocationSelectionSaving: true,
    };
  }),
  on(PrivateStorageActions.setLocationSelectionSuccess, PrivateStorageActions.setLocationSelectionFail, (state) => {
    return {
      ...state,
      isLocationSelectionSaving: false,
    };
  }),
  on(PrivateStorageActions.resetLocationSelection, (state) => {
    return {
      ...state,
      locationSelection: null,
      isLocationSelectionLoading: false,
      isLocationSelectionSaving: false,
    };
  }),
  on(PrivateStorageActions.testConnection, (state) => {
    return {
      ...state,
      isTesting: true,
      lastTestConnection: null,
    };
  }),
  on(PrivateStorageActions.testConnectionSuccess, (state, { response }) => {
    return {
      ...state,
      isTesting: false,
      lastTestConnection: response,
    };
  }),
  on(PrivateStorageActions.testConnectionFail, (state) => {
    return {
      ...state,
      isTesting: false,
      lastTestConnection: null,
    };
  }),
  on(PrivateStorageActions.resetTestConnection, (state) => {
    return {
      ...state,
      isTesting: false,
      lastTestConnection: null,
    };
  }),
);
